"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppInstallProgressSnapshotSchema = exports.APP_CONTAINER_TAKE_BACK_STATES = exports.APP_INSTALL_TERMINAL_STATUSES = exports.APP_INSTALL_STATUSES = exports.APP_INSTALL_STAGE_STATES = exports.APP_INSTALL_STAGES = exports.MARKETPLACE_APP_TARGET_PART_NOT_STORED = void 0;
exports.createAppInstallStages = createAppInstallStages;
exports.advanceAppInstallStage = advanceAppInstallStage;
exports.appInstallPercent = appInstallPercent;
/**
 * Progress of one App installation ("App hinzufügen"), shared by the server
 * job that runs it and every client that shows it.
 *
 * The stage list is fixed and known before the installation starts, so a
 * client can show every step from the first snapshot on. Stages only move
 * forward; a retry starts a new run that inspects the real target state
 * instead of replaying a stored cursor. The contract carries IDs only;
 * clients render labels and error texts from i18n.
 */
const zod_1 = require("zod");
/** Retryable refusal while the target page's part has not been stored yet. */
exports.MARKETPLACE_APP_TARGET_PART_NOT_STORED = 'MARKETPLACE_APP_TARGET_PART_NOT_STORED';
exports.APP_INSTALL_STAGES = [
    'check',
    'tables',
    'automations',
    'app',
    'views',
    'agentActions',
    'finish',
];
exports.APP_INSTALL_STAGE_STATES = [
    'pending',
    'running',
    'succeeded',
    'failed',
];
exports.APP_INSTALL_STATUSES = [
    'queued',
    'running',
    'retrying',
    'succeeded',
    'failed',
];
/** Statuses after which the snapshot never changes again. */
exports.APP_INSTALL_TERMINAL_STATUSES = new Set(['succeeded', 'failed']);
/**
 * What a failed App of an App Container import left once the import was
 * taken back: completed, recovery still needed, or a foreign draft preserved.
 */
exports.APP_CONTAINER_TAKE_BACK_STATES = [
    'complete',
    'incomplete',
    'draft_changed',
];
const StageProgressSchema = zod_1.z
    .object({
    id: zod_1.z.enum(exports.APP_INSTALL_STAGES),
    state: zod_1.z.enum(exports.APP_INSTALL_STAGE_STATES),
    current: zod_1.z.number().int().nonnegative().optional(),
    total: zod_1.z.number().int().positive().optional(),
})
    .strict()
    .refine(stage => (stage.current === undefined) === (stage.total === undefined) &&
    (stage.current === undefined || stage.current <= stage.total), { message: 'current and total come together and current <= total' });
exports.AppInstallProgressSnapshotSchema = zod_1.z
    .object({
    operationId: zod_1.z.string().min(1).max(191),
    status: zod_1.z.enum(exports.APP_INSTALL_STATUSES),
    stages: zod_1.z
        .array(StageProgressSchema)
        .length(exports.APP_INSTALL_STAGES.length)
        .refine(stages => stages.every((stage, index) => stage.id === exports.APP_INSTALL_STAGES[index]), { message: 'stages must list every App install stage in order' }),
    percent: zod_1.z.number().int().min(0).max(100),
    attempt: zod_1.z.number().int().positive(),
    maxAttempts: zod_1.z.number().int().positive(),
    failedStage: zod_1.z.enum(exports.APP_INSTALL_STAGES).optional(),
    errorCode: zod_1.z.string().min(1).max(128).optional(),
    /** Only on a failed App of an App Container import. */
    takeBack: zod_1.z.enum(exports.APP_CONTAINER_TAKE_BACK_STATES).optional(),
    result: zod_1.z
        .object({
        appBaseId: zod_1.z.string().min(1),
        installReceiptId: zod_1.z.string().min(1),
        mountIds: zod_1.z.array(zod_1.z.string().min(1)).readonly(),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine(snapshot => snapshot.takeBack === undefined || snapshot.status === 'failed', { message: 'only a failed installation names its take-back' });
/** Every stage pending: the view before the first stage starts. */
function createAppInstallStages() {
    return exports.APP_INSTALL_STAGES.map(id => ({ id, state: 'pending' }));
}
/**
 * Moves the installation to `stage`. Earlier stages become succeeded and
 * later ones stay pending; moving back to an earlier stage throws, because a
 * run never revisits a finished step.
 */
function advanceAppInstallStage(stages, stage, state, count) {
    if (stages.some(entry => entry.state === 'failed')) {
        throw new Error(`App install stage ${stage} cannot follow a failed stage`);
    }
    const target = exports.APP_INSTALL_STAGES.indexOf(stage);
    // The running stage may report again (a new count, its end); otherwise
    // the next stage is the first one that has not succeeded yet.
    const running = stages.findIndex(entry => entry.state === 'running');
    const earliest = running !== -1
        ? running
        : stages.filter(entry => entry.state === 'succeeded').length;
    if (target < earliest) {
        throw new Error(`App install stage ${stage} cannot follow ${exports.APP_INSTALL_STAGES[earliest - (running !== -1 ? 0 : 1)]}`);
    }
    return exports.APP_INSTALL_STAGES.map((id, index) => {
        if (index < target)
            return { id, state: 'succeeded' };
        if (index > target)
            return { id, state: 'pending' };
        return count ? { id, state, ...count } : { id, state };
    });
}
/**
 * Share of finished work: succeeded stages plus the counted part of the
 * running stage. Stays at 99 until the installation succeeded, so a full
 * bar always means a usable App.
 */
function appInstallPercent(stages, status) {
    if (status === 'succeeded')
        return 100;
    const done = stages.reduce((sum, stage) => {
        if (stage.state === 'succeeded')
            return sum + 1;
        if (stage.state === 'running' && stage.total)
            return sum + (stage.current ?? 0) / stage.total;
        return sum;
    }, 0);
    return Math.min(99, Math.floor((done / exports.APP_INSTALL_STAGES.length) * 100));
}
